import PDFMarkdown, { UserDefinedOverride } from './markdown'
import React from 'react'
import { useMarkdownString } from '../../components/Markdown/hooks'
import { PdfContextProps } from './types'

/**
 * OptionalPDFMarkdown - will render the children as markdown if it is a markdown string (or if interpretAsMarkdown is true)
 *
 * @param {Object} props - the react props
 * @param {string} props.children - the markdown string
 * @param {ReactPDF} props.ReactPdf - the reactPdf module
 * @param {pdfStyles} props.pdfStyles - the current reactPdf stylesheet
 * @param {?Array<string>} props.disabledComponents? - a list of components to refuse to render
 * @param {?UserDefinedOverride} props.overrides? - a mapping of components names to the components which should be used
 * @param {string} props.color - the font color to use in the markdown
 * @param {?boolean} props.interpretAsMarkdown? - if true, will interpret the children as markdown. If false, will not interpret the children as markdown. If left unset, will check the children to determine if it is markdown.
 *
 * @return {React$Node}
 */
export default function OptionalPDFMarkdown({
	children,
	ReactPdf,
	pdfStyles,
	interpretAsMarkdown,
	color,
	overrides,
}: {
	children: string
	interpretAsMarkdown?: boolean | null
	color?: string | null | undefined
	overrides?: UserDefinedOverride | null | undefined
} & PdfContextProps): JSX.Element {
	const markdownString = useMarkdownString(children)

	if (
		interpretAsMarkdown === true ||
		(markdownString != null && interpretAsMarkdown !== false)
	) {
		return (
			<PDFMarkdown
				markdown={markdownString ?? children}
				ReactPdf={ReactPdf}
				pdfStyles={pdfStyles}
				color={color}
				overrides={overrides}
			/>
		)
	}

	return <ReactPdf.Text>{children}</ReactPdf.Text>
}
